import React, { useState } from 'react';
import { Device } from '../../lib/types';
import Modal, { ModalTitle, ModalActions } from '../../components/Modal/Modal';
import Button from '../../components/Button/Button';
import styles from './Devices.module.scss';

interface WrongAdopterInfoModalProps {
  open: boolean;
  /** Dismiss the flow entirely (X / backdrop / Escape). */
  onClose: () => void;
  /** Return to the previous step — the cancel-reason picker. */
  onBack: () => void;
  device: Device | null;
  /** Cancels the transfer and reopens the adopter screen prefilled. */
  onConfirm: (device: Device) => Promise<void>;
}

/**
 * Intermediate confirmation shown after the rescue picks "I entered the wrong
 * adopter info" in the cancel-reason modal. It spells out what confirming does
 * (device back to stock, pet stays attached, adoption screen reopens prefilled)
 * before the transfer is actually cancelled.
 */
export default function WrongAdopterInfoModal({
  open,
  onClose,
  onBack,
  device,
  onConfirm,
}: WrongAdopterInfoModalProps) {
  const [submitting, setSubmitting] = useState(false);

  if (!device) {
    return null;
  }

  const currentDevice = device;

  async function handleConfirm() {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    try {
      // Await the mutation so a failure keeps the modal open for retry — the
      // error toast is raised by the handler in Devices, which re-throws.
      await onConfirm(currentDevice);
      onClose();
    } catch {
      // Kept open on failure; toast already surfaced the error.
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} onClose={submitting ? () => {} : onClose}>
      <ModalTitle>Re-enter adopter details</ModalTitle>
      <p className={styles.modalDescription}>
        You picked <b>&ldquo;I entered the wrong adopter info.&rdquo;</b> Here&rsquo;s what happens when you confirm:
      </p>
      <div className={styles.outcomeBox}>
        <div className={styles.outcomeBoxIcon}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
            <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" />
          </svg>
        </div>
        <div>
          <div className={styles.outcomeBoxLabel}>What happens</div>
          <div className={styles.outcomeBoxText}>
            Device returns to stock
            {device.petName ? (
              <>
                {' '}
                with <b>{device.petName}</b> still attached
              </>
            ) : (
              <> with the pet still attached</>
            )}
            , and the adoption screen reopens prefilled so you can correct the details and re-send.
          </div>
        </div>
      </div>
      <ModalActions>
        <Button variant="ghost" onClick={onBack} disabled={submitting}>
          Back
        </Button>
        <Button variant="green" onClick={handleConfirm} disabled={submitting}>
          {submitting ? 'Working…' : 'Confirm — fix & re-send'}
        </Button>
      </ModalActions>
    </Modal>
  );
}
